<template>
<DropdownPanel ref="MenuPanel" data-component="MenuNavbar.vue" class="global-menu" Direction="bottom right">
	<template #button>
		<OIcon v-if="burger" name="burger" size="1.5"/>
		<template v-else>
			<BaseAvatar :user="userAvatar" size="sm" class="mr-3" data-test="avatar"/>
			<span data-test="profile" v-html="'Menu'"/>
			<OIcon name="chevron-down" size="0.5" class="mx-2"/>
		</template>
	</template>

	<div>
		<OnboardingProgress v-if="userStore.isCustomer" class="mx-4 mb-8 mt-4" :displayButton="true"/>
		<template v-for="section in menu" :key="section.sectionName">
			<div v-if="section.sectionName" class="px-4 py-3 text-base font-bold" v-html="section.sectionName"/>
			<div v-else class="h-6"/>

			<div class="divide-y divide-neutral-100">
				<div v-for="item in section.items" :key="item.name">
					<div :class="itemClass" :data-test="addTestName('global_menu',item.name)" @click="itemClick(item)">
						<OIcon :name="item.icon" :set="item.iconSet ? item.iconSet : 'menu'" size="1.25" class="mr-2 text-info-200"/>
						<span v-html="item.name"/>

						<div v-if="item.routerName == 'CustomerProfilePromotion' && isPromotion2in1Active" class="ml-2 inline-flex h-[18px] items-center justify-start gap-1 rounded-lg bg-success-100 px-2">
							<div class="text-xs font-bold leading-[18px] text-success-600">
								2 w cenie 1
							</div>
						</div>
						<OIcon v-if="item.items" name="chevron-right" size="0.75" class="ml-auto text-neutral-900" :class="{'rotate-90': item.items && !item.isExpanded, '-rotate-90': item.isExpanded }"/>
					</div>
					<div v-if="item.isExpanded" class="pl-6">
						<div v-for="subItem in item.items" :key="subItem.name" :data-test="addTestName('global_menu__sub',subItem.name)" :class="itemClass" @click="itemClick(subItem)">
							<OIcon :name="subItem.icon" :set="item.iconSet ? item.iconSet : 'menu'" size="1.25" class="mr-2 text-info-200"/>
							<span v-html="subItem.name"/>
							<OIcon name="chevron-right" size="0.75" class="ml-auto text-neutral-900"/>
						</div>
					</div>
				</div>
			</div>
		</template>
		<div v-if="ScreenSize < 1" class="h-16"/>
	</div>
</DropdownPanel>
</template>

<script lang="ts">
import { mapStores, mapState, mapWritableState } from 'pinia';

import DropdownPanel from '@globals/components/global/DropdownPanel.vue';
import { useAppStore } from '@globals/stores/app';
import { useUserStore } from '@globals/stores/user';
import { AppModules } from '@globals/types/AppModules';
import { MenuNavbarItem } from '@globals/types/components/MenuNavbarItem';
import { addTestName } from '@globals/utilities/DataTestAttribs';

import OnboardingProgress from '@customer/onboarding/OnboardingProgress.vue';
import { useCustomerProfileViewStore } from '@customer/stores/customerProfileView';
import { usePromotionStore } from '@customer/stores/promotion';
import { useServicesStore } from '@customer/stores/services';

/**
 * Komponent odpowiedzialny za przycisk z głównym menu nawigacyjnym.
 */
export default {
	name: 'MenuNavbar',
	components: {
		DropdownPanel,
		OnboardingProgress,
	},
	props: {
		/**
		 * Czy pokazać przycisk w formie skróconego przycisku?
		 */
		burger: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			itemClass: 'flex items-center hover:text-info cursor-pointer px-4 py-3',
			customerMenu: [
				{
					sectionName: 'Profil',
					items: [
						{
							name: 'Wizytówka',
							routerName: this.ScreenSize > 2 ? 'CustomerProfileBasicInfo' : 'CustomerProfile',
							icon: 'profile-main',
							isExpanded: false,
							items: [
								{
									name: 'Informacje ogólne',
									routerName: 'CustomerProfileBasicInfo',
									icon: 'profile-basic-info',
									url: '/konto/profil/informacje',
								},
								{
									name: 'Kontakt',
									routerName: 'CustomerProfileContactInfo',
									icon: 'profile-contact-info',
									url: '/konto/profil/dane-kontaktowe',
								},
								{
									name: 'Opis',
									routerName: 'CustomerProfileDescription',
									icon: 'profile-description',
									url: '/konto/profil/opis',
								},
								{
									name: 'Zdjęcia',
									routerName: 'CustomerProfileGallery',
									icon: 'profile-photos',
									url: '/konto/profil/galeria-zdjec',
								},
								{
									name: 'Opinie',
									routerName: 'CustomerProfileReviews',
									icon: 'profile-reviews',
									url: '/konto/profil/opinie',
								},
								{
									name: 'Oferty',
									routerName: 'CustomerProfileOffers',
									icon: 'profile-offers',
									url: '/konto/profil/oferty',
								},
							],
						},
						{
							name: 'Promuj firmę',
							routerName: 'CustomerProfilePromotion',
							icon: 'profile-promotion',
							url: '/konto/profil/promowanie',
						},
					],
				},
				{
					sectionName: 'Konto',
					items: [
						{
							name: 'Powiadomienia',
							travelTo: ['CustomerSettingsView', { Section: 'Notifications' }],
							icon: 'account-notifications',
							url: '/konto/ustawienia/powiadomienia',
						},
						{
							name: 'Ustawienia konta',
							travelTo: ['CustomerSettingsView', { Section: 'ChangePassword' }],
							icon: 'account-settings',
							url: '/konto/ustawienia/haslo',
						},
						{
							name: 'Terminarz',
							routerName: 'SchedulerWrapper',
							icon: 'account-scheduler',
							url: '/konto/terminarz',
						},
						{
							name: 'Pomoc',
							icon: 'account-help',
							url: '/kontakt.html?idSubject=contactError',
						},
					],
				},
				{
					sectionName: 'Pozostałe',
					items: [
						{
							name: 'Certyfikaty',
							routerName: 'CustomerProfileCertificates',
							icon: 'other-certificates',
							url: '/konto/certyfikaty',
						},
						{
							name: 'Najlepsi 2025',
							routerName: 'CustomerProfileCertificatesBadge',
							icon: 'other-the-best',
							url: '/konto/certyfikaty/odznaki',
						},
						{
							name: 'Program Partnerski',
							routerName: 'CustomerProfilePartnership',
							icon: 'other-partnership',
							url: '/konto/program-partnerski',
						},
					],
				},
			],
			adminMenu: [
				{
					sectionName: 'Administracja',
					items: [
						{
							name: 'Obsługa bieżąca',
							icon: '',
							url: '/admin/WFObjectList.ofer',
						},
						{
							name: 'Rejestracja dostawcy',
							icon: '',
							url: '/rejestracja/wybierz-klientow',
						},
					],
				},
			],
		};
	},
	computed: {
		...mapStores(useUserStore),
		...mapWritableState(useCustomerProfileViewStore, [
			'isVueRouterView',
			'isFormChanged',
			'isDataShouldSubmit',
		]),
		...mapState(useServicesStore, [
			'isFilterDirty',
		]),
		...mapWritableState(useServicesStore, [
			'displaySaveChanges',
		]),
		...mapWritableState(useAppStore, [
			'nextRouteNameAttempt',
		]),
		...mapState(usePromotionStore, [
			'isPromotion2in1Active',
		]),
		menu() {
			const result = [];

			const currentModule = this.CurrentModule;
			const { role } = this.userStore;

			if (role === 'BUYER') {
				result.push(this.generateBuyerMenu());
			} else {
				result.push(...this.customerMenu);
			}

			if (role === 'ADMIN') {
				result.push(...this.adminMenu);
			}

			result.push(this.generateFooterMenu(role, currentModule));

			return result;
		},
		userAvatar() {
			return {
				Avatar: this.userStore.avatar,
				FirstName: this.userStore.firstName,
				LastName: this.userStore.lastName,
			};
		},
	},
	methods: {
		addTestName,
		/**
		 * Przenosi użytkownika na wskazaną stronę.
		 */
		itemClick(item: MenuNavbarItem) {
			// Jeżeli element ma podelementy, to je rozwiń.
			if (item.items) {
				item.isExpanded = !item.isExpanded;
				return;
			}

			// tutaj sprawdzamy czy ktoś ma nie zapisane zmiany i czy chce przejść na inną stonę która nie jest jeszcze w vue routerze
			if (this.isFormChanged || this.isFilterDirty) {
				if ((this.$route.name as string)?.startsWith('CustomerProfile')) {
					this.isDataShouldSubmit = true;
				} else {
					this.displaySaveChanges = true;
				}

				this.nextRouteNameAttempt = item.travelTo && item.travelTo[0] ? item.travelTo[0] : item.routerName;

				return;
			}

			if (item.routerName) {
				// Jeżeli chcemy przejść na profil z innego modułu np. Admin lub PublicView
				if (this.CurrentModule !== AppModules.MY_OFERTEO) {
					window.location.href = item.url || 'konto/profil';

					return;
				}

				// Jeżeli to pierwsze wejście na stronę z Vue Routerem, to użyj replace
				this.$router.push({ name: item.routerName, replace: !this.isVueRouterView });
				this.isVueRouterView = true;

				(this.$refs.MenuPanel as typeof DropdownPanel).Toggle();

				return;
			}

			const currentModule = this.CurrentModule;
			const targetModule = item.targetModule || 'CustomerView';
			(this.$refs.MenuPanel as typeof DropdownPanel).Toggle();

			// Jeżeli jest to element wylogowywujący, to to zrób.
			if (item.travelTo && item.travelTo[0] === 'Logout') {
				this.userStore.logout();
				return;
			}

			// Jeżeli element nie prowadzi na żadną ścieżkę lub prowadzi
			// na inny moduł, to przejdź pod wskazany URL.
			if (item.url && (!item.travelTo || currentModule !== targetModule)) {
				document.location.href = item.url;
				return;
			}

			this.State.Travel(...item.travelTo);
		},

		generateFooterMenu(role: string, currentModule: string) {
			const footer = {
				sectionName: '',
				items: [
					{
						name: 'Strona główna Oferteo',
						icon: 'main-home',
						url: '/',
					},
				] as MenuNavbarItem[],
			};

			if (role !== 'BUYER' && currentModule !== 'BuyerView') {
				footer.items.push({
					name: 'Przełącz na profil zleceniodawcy',
					icon: 'main-switch',
					url: '/konto/moje-zapytania/zlecone',
					targetModule: 'BuyerView',
				});
			}

			footer.items.push({
				name: 'Wyloguj',
				icon: 'main-logout',
				travelTo: ['Logout', {}],
			});

			return footer;
		},

		generateBuyerMenu() {
			const buyerMenu = {
				sectionName: '&nbsp;',
				items: [
					{
						name: 'Moje zapytania',
						icon: 'done-check-notes',
						iconSet: 'base',
						url: '/konto/moje-zapytania/zlecone',
					},
				] as MenuNavbarItem[],
			};

			const isLogged = this.userStore.userId > 0;
			const { isAuth } = this.userStore;
			if (isLogged && isAuth) {
				buyerMenu.items.push({
					name: 'Dane kontaktowe',
					icon: 'user-circle-new',
					iconSet: 'base',
					url: '/rfp/dane-kontaktowe.ofer',
				});
			}

			const { isCustomer } = this.userStore;
			buyerMenu.items.push(isCustomer
				? {
					name: 'Zaloguj się jako Specjalista',
					icon: 'main-switch',
					url: '/logowanie',
				}
				: {
					name: 'Dołącz do Specjalistów',
					icon: 'main-switch',
					url: '/rejestracja/wybierz-klientow',
				});

			buyerMenu.items.push({
				name: 'Pomoc',
				icon: 'account-help',
				url: '/kontakt.html?idSubject=contactError',
			});

			return buyerMenu;
		},
	},
};

</script>

<style lang="scss">
.global-menu .dropdown-panel {
	padding: 0;
	overflow-y: auto;
}

.global-navigation .global-menu .dropdown-panel {
	top: 3rem;
	width: 22.5rem;
	max-height: calc(100vh - 6rem);
}

.page-header .dropdown-wrapper {
	display: flex;
}

.page-header .global-menu .dropdown-panel {
	position: fixed;
	top: 4rem;
	right: 0;
	left: 0;
	width: 100vw;
	max-height: calc(100vh - 7rem);
	margin-right: 0;
}
</style>
